import { useEffect, useRef, useState } from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import Button from "../Button";
import Swal from 'sweetalert2';
import ImageOverlayModal from "./ImageOverlayModal";
import { TARGET_DIMENSIONS } from "../MovieRowForm/HighlightRowBuilder";

const resolveUrl = (file, base) => {
  if (file instanceof File) return URL.createObjectURL(file);
  if (!file) return "";
  if (file.startsWith("http")) return file;
  const path = file.startsWith("tiles/") ? file : `tiles/${file}`;
  return `${base}${path}`;
};

// A small trigger button per field-section, opening the shared overlay+text
// modal against that field's currently pinned/first image — not a per-field
// picker of its own, so each block above stays exactly as it was.
const OverlayEditButton = ({ field, label, currentImages, pinnedIndex, minioBaseUrl, targetDims, onOpen }) => {
  const baseImage = currentImages?.[pinnedIndex] ?? currentImages?.[0] ?? null;
  if (!baseImage) {
    return (
      <div className="w-full text-xs text-gray-400 mt-2">
        Add a {label.toLowerCase()} image above to overlay an image or text onto it.
      </div>
    );
  }
  return (
    <div className="w-full mt-2 border-t pt-2">
      <button
        type="button"
        className="text-xs text-indigo-600 hover:text-indigo-800 underline"
        onClick={() => onOpen({ field, label, baseUrl: resolveUrl(baseImage, minioBaseUrl), targetDims })}
      >
        Overlay image / add text onto this {label.toLowerCase()}
      </button>
    </div>
  );
};

// One image field (Poster / Square / Portrait / Long Portrait / Title Logo /
// Background) rendered as an "album" grid — up to 6 covers per row on wider
// screens — instead of a cramped single-line row of tiny squares. The upload
// input, pin/remove interactions, and delete-confirm dialog are unchanged;
// only the visual layout of the thumbnails changed.
const confirmAndRemoveImage = (file, field, idx, label, handleRemoveImage, pinnedRowRefs) => {
  const pinnedRows = typeof file === "string" ? pinnedRowRefs?.[file] || [] : [];
  if (pinnedRows.length > 0) {
    const rowNames = pinnedRows.map((r) => r.row_header || `Row ${r.id}`).join(", ");
    Swal.fire({
      icon: "error",
      title: "Image In Use",
      text: `This image is assigned to ${pinnedRows.length > 1 ? "rows" : "row"} "${rowNames}" and can't be deleted while it's pinned there. Unpin it from the row first.`,
    });
    return;
  }
  Swal.fire({
    title: 'Are you sure?',
    text: `Do you want to delete this ${label}?`,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#3085d6',
    cancelButtonColor: '#d33',
    confirmButtonText: 'Yes, delete it!'
  }).then((result) => {
    if (result.isConfirmed) {
      handleRemoveImage(field, idx);
    }
  });
};

const ImageFieldGrid = ({
  field,
  label,
  aspectClass = "aspect-[3/4]",
  formData,
  handleFileChange,
  handleRemoveImage,
  pinnedImageIndex,
  setPinnedImageIndex,
  minioBaseUrl,
  emptyState = null,
  overlay = null,
  pinnedRowRefs = {},
}) => {
  const images = formData[field] || [];
  return (
    <div className="col-span-1 sm:col-span-2 p-4 rounded-xl border border-gray-100 bg-white">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <label className="text-sm font-semibold text-gray-700">{label}</label>
        <input
          type="file"
          name={field}
          multiple
          accept="image/*"
          onChange={handleFileChange}
          className="text-xs text-gray-500 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:bg-indigo-500 file:text-white hover:file:bg-indigo-600"
        />
      </div>

      {images.length > 0 || emptyState ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
          {images.map((file, idx) => {
            const isPinned = pinnedImageIndex[field] === idx;
            const rowRefs = typeof file === "string" ? pinnedRowRefs[file] || [] : [];
            const isPinnedToRow = rowRefs.length > 0;
            return (
              <div key={idx} className="flex flex-col gap-1">
                <div
                  className={`group relative ${aspectClass} rounded-lg overflow-hidden cursor-pointer shadow-sm ring-1 ring-black/5 transition-transform hover:scale-[1.03] ${isPinned ? "ring-2 ring-blue-500" : ""
                    }`}
                  onClick={() => setPinnedImageIndex((prev) => ({ ...prev, [field]: idx }))}
                >
                  <img
                    src={resolveUrl(file, minioBaseUrl)}
                    alt={`${label} ${idx}`}
                    draggable={false}
                    className="w-full h-full object-cover"
                  />
                  {isPinned && (
                    <span className="absolute top-1 left-1 bg-blue-600 text-white text-[10px] font-medium px-1.5 py-0.5 rounded">
                      Pinned
                    </span>
                  )}
                  <button
                    type="button"
                    className="absolute top-1 right-1 bg-black/60 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={(e) => {
                      e.stopPropagation();
                      confirmAndRemoveImage(file, field, idx, label, handleRemoveImage, pinnedRowRefs);
                    }}
                  >
                    ✕
                  </button>
                </div>
                {isPinnedToRow && (
                  <span
                    className="bg-blue-600 text-white text-[10px] font-medium px-1.5 py-0.5 rounded text-center truncate"
                    title={`Assigned to: ${rowRefs.map((r) => r.row_header || r.id).join(", ")}`}
                  >
                    used: in {rowRefs.map((r) => r.row_header || r.id).join(", ")}
                  </span>
                )}
              </div>
            );
          })}
          {images.length === 0 && emptyState}
        </div>
      ) : (
        <p className="text-xs text-gray-400 italic">No images uploaded yet</p>
      )}

      {overlay}
    </div>
  );
};

const DEFAULT_BG_URL = "https://images.do.cloudwalker.tv/images/globalbg/default_bg.webp";

const BANNER_GROUPS = [
  { field: "banner", label: "Masthead Banner", width: 1920, height: 500 },
  { field: "featureBanner", label: "Feature Banner", width: 1920, height: 300 },
  { field: "heroBanner", label: "Hero Banner", width: 1920, height: 900 },
];

const StepImageUpload = ({
  formData,
  handleFileChange,
  handleRemoveImage,
  setPinnedImageIndex,
  pinnedImageIndex,
  moveBannerImage,
  minioBaseUrl,
  onAddGeneratedImage,
  handleBack,
  handleNext,
  pinnedRowRefs = {},
}) => {
  // { field, label, baseUrl } for whichever field's overlay+text modal is
  // currently open, or null when closed.
  const [overlayModal, setOverlayModal] = useState(null);

  // Reads each banner image's real pixel size on load — the upload inputs never
  // recorded this, which is how images ended up filed under the wrong banner group.
  const dimensionCacheRef = useRef(new Map());
  const [dimensionTick, setDimensionTick] = useState(0);
  const idCacheRef = useRef(new Map());
  const idCounterRef = useRef(0);

  const getItemId = (file) => {
    if (!idCacheRef.current.has(file)) {
      idCounterRef.current += 1;
      idCacheRef.current.set(file, `banner-item-${idCounterRef.current}`);
    }
    return idCacheRef.current.get(file);
  };

  // Tracks real mount state (not per-effect-run state) so a dimension probe
  // started before a React 18 StrictMode dev double-invoke still lands its
  // result — gating on a per-run "cancelled" closure left probes stuck in
  // "loading" forever because the second run saw the cache already claimed.
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    BANNER_GROUPS.forEach(({ field }) => {
      (formData[field] || []).forEach((file) => {
        if (!file || dimensionCacheRef.current.has(file)) return;
        dimensionCacheRef.current.set(file, { status: "loading" });
        const img = new window.Image();
        img.onload = () => {
          if (!isMountedRef.current) return;
          dimensionCacheRef.current.set(file, {
            status: "loaded",
            width: img.naturalWidth,
            height: img.naturalHeight,
          });
          setDimensionTick((t) => t + 1);
        };
        img.onerror = () => {
          if (!isMountedRef.current) return;
          dimensionCacheRef.current.set(file, { status: "error" });
          setDimensionTick((t) => t + 1);
        };
        img.src = resolveUrl(file, minioBaseUrl);
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.banner, formData.featureBanner, formData.heroBanner, minioBaseUrl]);

  // Flat pool combining all three banner fields — the single drag source.
  const poolItems = BANNER_GROUPS.flatMap(({ field }) =>
    (formData[field] || []).map((file, index) => ({ file, field, index }))
  );

  const handleBannerDragEnd = (result) => {
    const { source, destination } = result;
    if (!destination) return;
    if (source.droppableId !== "bannerPool") return;
    if (destination.droppableId === "bannerPool") return;

    const dragged = poolItems[source.index];
    if (!dragged) return;

    const destIndex = (formData[destination.droppableId] || []).length;
    moveBannerImage?.(dragged.field, destination.droppableId, dragged.index, destIndex);
  };

  return (
    <>
      <div className="text-center">


      </div>
      {/* Poster, Portrait, Masthead Banner, Feature Banner, Hero Banner, Title Logo, and Background */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Poster */}
        <ImageFieldGrid
          field="poster"
          label="Poster"
          aspectClass="aspect-video"
          formData={formData}
          handleFileChange={handleFileChange}
          handleRemoveImage={handleRemoveImage}
          pinnedImageIndex={pinnedImageIndex}
          setPinnedImageIndex={setPinnedImageIndex}
          minioBaseUrl={minioBaseUrl}
          pinnedRowRefs={pinnedRowRefs}
          overlay={
            <OverlayEditButton
              field="poster"
              label="Poster"
              currentImages={formData.poster}
              pinnedIndex={pinnedImageIndex.poster ?? 0}
              minioBaseUrl={minioBaseUrl}
              targetDims={TARGET_DIMENSIONS.poster}
              onOpen={setOverlayModal}
            />
          }
        />
        {/* Square */}
        <ImageFieldGrid
          field="square"
          label="Square"
          aspectClass="aspect-square"
          formData={formData}
          handleFileChange={handleFileChange}
          handleRemoveImage={handleRemoveImage}
          pinnedImageIndex={pinnedImageIndex}
          setPinnedImageIndex={setPinnedImageIndex}
          minioBaseUrl={minioBaseUrl}
          pinnedRowRefs={pinnedRowRefs}
          overlay={
            <OverlayEditButton
              field="square"
              label="Square"
              currentImages={formData.square}
              pinnedIndex={pinnedImageIndex.square ?? 0}
              minioBaseUrl={minioBaseUrl}
              onOpen={setOverlayModal}
            />
          }
        />
        {/* Portrait */}
        <ImageFieldGrid
          field="portrait"
          label="Portrait"
          aspectClass="aspect-[3/4]"
          formData={formData}
          handleFileChange={handleFileChange}
          handleRemoveImage={handleRemoveImage}
          pinnedImageIndex={pinnedImageIndex}
          setPinnedImageIndex={setPinnedImageIndex}
          minioBaseUrl={minioBaseUrl}
          pinnedRowRefs={pinnedRowRefs}
          overlay={
            <OverlayEditButton
              field="portrait"
              label="Portrait"
              currentImages={formData.portrait}
              pinnedIndex={pinnedImageIndex.portrait ?? 0}
              minioBaseUrl={minioBaseUrl}
              targetDims={TARGET_DIMENSIONS.portrait}
              onOpen={setOverlayModal}
            />
          }
        />
        {/* Long Portrait */}
        <ImageFieldGrid
          field="longPortrait"
          label="Long Portrait"
          aspectClass="aspect-[3/4]"
          formData={formData}
          handleFileChange={handleFileChange}
          handleRemoveImage={handleRemoveImage}
          pinnedImageIndex={pinnedImageIndex}
          setPinnedImageIndex={setPinnedImageIndex}
          minioBaseUrl={minioBaseUrl}
          pinnedRowRefs={pinnedRowRefs}
          overlay={
            <OverlayEditButton
              field="longPortrait"
              label="Long Portrait"
              currentImages={formData.longPortrait}
              pinnedIndex={pinnedImageIndex.longPortrait ?? 0}
              minioBaseUrl={minioBaseUrl}
              onOpen={setOverlayModal}
            />
          }
        />

        <div className="col-span-1 sm:col-span-2 border border-gray-100 rounded-xl p-4 bg-white space-y-5">
        <DragDropContext onDragEnd={handleBannerDragEnd}>
          {BANNER_GROUPS.map((group) => (
            <div key={group.field}>
              <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                <label className="text-sm font-semibold text-gray-700">
                  {group.label}
                  <span className="ml-2 text-xs font-normal text-gray-400">
                    ({group.width} × {group.height})
                  </span>
                </label>
                <input
                  type="file"
                  name={group.field}
                  multiple
                  accept="image/*"
                  onChange={handleFileChange}
                  className="text-xs text-gray-500 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:bg-indigo-500 file:text-white hover:file:bg-indigo-600"
                />
              </div>
              <Droppable droppableId={group.field}>
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className={`grid grid-cols-3 gap-3 min-h-[80px] rounded-xl border-2 border-dashed p-2 ${snapshot.isDraggingOver ? "border-indigo-400 bg-indigo-50" : "border-gray-200"
                      }`}
                  >
                    {(formData[group.field] || []).map((file, idx) => {
                      const isPinned = pinnedImageIndex[group.field] === idx;
                      const rowRefs = typeof file === "string" ? pinnedRowRefs[file] || [] : [];
                      const isPinnedToRow = rowRefs.length > 0;
                      return (
                        <div
                          key={idx}
                          className={`group relative rounded-lg overflow-hidden cursor-pointer shadow-sm ring-1 ring-black/5 transition-transform hover:scale-[1.03] ${isPinned ? "ring-2 ring-blue-500" : ""
                            }`}
                          style={{ aspectRatio: `${group.width} / ${group.height}` }}
                          onClick={() =>
                            setPinnedImageIndex((prev) => ({ ...prev, [group.field]: idx }))
                          }
                        >
                          <img
                            src={resolveUrl(file, minioBaseUrl)}
                            alt={`${group.label} Preview ${idx}`}
                            draggable={false}
                            className="w-full h-full object-cover"
                          />
                          {isPinned && (
                            <span className="absolute top-1 left-1 bg-blue-600 text-white text-[10px] font-medium px-1.5 py-0.5 rounded">
                              Pinned
                            </span>
                          )}
                          {isPinnedToRow && (
                            <span
                              className="absolute bottom-1 left-1 right-1 bg-blue-600 text-white text-[10px] font-medium px-1.5 py-0.5 rounded text-center truncate"
                              title={`Assigned to: ${rowRefs.map((r) => r.row_header || r.id).join(", ")}`}
                            >
                              In row: {rowRefs.map((r) => r.row_header || r.id).join(", ")}
                            </span>
                          )}
                          <button
                            type="button"
                            className="absolute top-1 right-1 bg-black/60 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                            onClick={(e) => {
                              e.stopPropagation();
                              confirmAndRemoveImage(file, group.field, idx, group.label, handleRemoveImage, pinnedRowRefs);
                            }}
                          >
                            ✕
                          </button>
                        </div>
                      );
                    })}
                    {provided.placeholder}
                    {(formData[group.field] || []).length === 0 && (
                      <p className="col-span-3 text-xs text-gray-400 italic py-4 text-center">
                        No {group.label.toLowerCase()} uploaded yet
                      </p>
                    )}
                  </div>
                )}
              </Droppable>
              <OverlayEditButton
                field={group.field}
                label={group.label}
                currentImages={formData[group.field]}
                pinnedIndex={pinnedImageIndex[group.field] ?? 0}
                minioBaseUrl={minioBaseUrl}
                targetDims={{ width: group.width, height: group.height }}
                onOpen={setOverlayModal}
              />
            </div>
          ))}

          {/* Banner Images — flat pool, drag one up into the correct box above */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              Banner Images
            </label>
            <p className="text-xs text-gray-400 mb-3">
              Every Masthead / Feature / Hero banner image uploaded above is grouped here. Drag one
              into the box above that its resolution actually matches.
            </p>
            <Droppable droppableId="bannerPool" direction="horizontal">
              {(provided, snapshot) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className={`grid grid-cols-3 gap-3 min-h-[110px] rounded-xl p-3 border-2 border-dashed transition-colors ${snapshot.isDraggingOver ? "border-indigo-400 bg-indigo-50" : "border-gray-200"
                    }`}
                >
                  {poolItems.map((item, idx) => {
                    const itemId = getItemId(item.file);
                    const dim = dimensionCacheRef.current.get(item.file);
                    return (
                      <Draggable key={itemId} draggableId={itemId} index={idx}>
                        {(dragProvided, dragSnapshot) => (
                          <div
                            ref={dragProvided.innerRef}
                            {...dragProvided.draggableProps}
                            {...dragProvided.dragHandleProps}
                            className={`cursor-grab active:cursor-grabbing ${dragSnapshot.isDragging ? "ring-2 ring-indigo-400 rounded-lg" : ""
                              }`}
                          >
                            <img
                              src={resolveUrl(item.file, minioBaseUrl)}
                              alt=""
                              draggable={false}
                              style={{
                                aspectRatio:
                                  dim?.status === "loaded" ? `${dim.width} / ${dim.height}` : "16 / 5",
                              }}
                              className="w-full object-cover rounded-lg shadow-sm ring-1 ring-black/5 border-2 border-transparent hover:border-indigo-400 transition-colors"
                            />
                            <div className="text-center text-[11px] text-gray-500 mt-1">
                              {dim?.status === "loaded"
                                ? `${dim.width} × ${dim.height}`
                                : dim?.status === "error"
                                  ? "—"
                                  : "…"}
                            </div>
                          </div>
                        )}
                      </Draggable>
                    );
                  })}
                  {provided.placeholder}
                  {poolItems.length === 0 && (
                    <p className="col-span-3 text-xs text-gray-400 italic py-8 text-center">
                      No banner images uploaded yet
                    </p>
                  )}
                </div>
              )}
            </Droppable>
          </div>
        </DragDropContext>
        </div>

        {/* Title Logo */}
        <ImageFieldGrid
          field="titleLogo"
          label="Title Logo"
          aspectClass="aspect-video"
          formData={formData}
          handleFileChange={handleFileChange}
          handleRemoveImage={handleRemoveImage}
          pinnedImageIndex={pinnedImageIndex}
          setPinnedImageIndex={setPinnedImageIndex}
          minioBaseUrl={minioBaseUrl}
          pinnedRowRefs={pinnedRowRefs}
          overlay={
            <OverlayEditButton
              field="titleLogo"
              label="Title Logo"
              currentImages={formData.titleLogo}
              pinnedIndex={pinnedImageIndex.titleLogo ?? 0}
              minioBaseUrl={minioBaseUrl}
              onOpen={setOverlayModal}
            />
          }
        />
        {/* Background */}
        <ImageFieldGrid
          field="background"
          label="Background"
          aspectClass="aspect-video"
          formData={formData}
          handleFileChange={handleFileChange}
          handleRemoveImage={handleRemoveImage}
          pinnedImageIndex={pinnedImageIndex}
          setPinnedImageIndex={setPinnedImageIndex}
          minioBaseUrl={minioBaseUrl}
          pinnedRowRefs={pinnedRowRefs}
          emptyState={
            (!formData.background || formData.background.length === 0) && (
              <div className="relative aspect-video rounded-lg overflow-hidden ring-1 ring-black/5">
                <img
                  src={DEFAULT_BG_URL}
                  alt="Default Background"
                  className="w-full h-full object-cover opacity-40"
                />
                <span className="absolute bottom-1 left-1 right-1 text-center text-xs text-gray-500 bg-white bg-opacity-70 rounded px-1">
                  Default
                </span>
              </div>
            )
          }
          overlay={
            <OverlayEditButton
              field="background"
              label="Background"
              currentImages={formData.background}
              pinnedIndex={pinnedImageIndex.background ?? 0}
              minioBaseUrl={minioBaseUrl}
              targetDims={TARGET_DIMENSIONS.background}
              onOpen={setOverlayModal}
            />
          }
        />
      </div>

      {overlayModal && (
        <ImageOverlayModal
          baseImageUrl={overlayModal.baseUrl}
          targetDims={overlayModal.targetDims}
          onClose={() => setOverlayModal(null)}
          onSave={(file) => {
            onAddGeneratedImage(overlayModal.field, file);
            setOverlayModal(null);
          }}
        />
      )}
    </>
  );
};

export default StepImageUpload;
